import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const monacoYachtShow: ChauffeurPage = {
  slug: "monaco-yacht-show",
  title: "Private chauffeur Monaco Yacht Show – UHNW VTC",
  subtitle:
    "Private chauffeur during the Monaco Yacht Show: airport ↔ Port Hercule transfers, owner and crew runs, discreet UHNW service.",
  keywords: [
    "monaco yacht show chauffeur",
    "monaco yacht show private chauffeur",
    "monaco yacht show vtc",
  ],
  hero: {
    title: "Private chauffeur during the Monaco Yacht Show",
    subtitle:
      "In late September the superyacht industry gathers at Port Hercule: a discreet chauffeur for owners, brokers and crews.",
    keywords: [
      "Monaco Yacht Show chauffeur",
      "Port Hercule VTC",
      "Monaco superyacht chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Monaco Yacht Show",
    badges: ["Airport ↔ Port Hercule", "Crew runs", "UHNW discretion"],
  },
  benefits: {
    heading: "Why a chauffeur during the Yacht Show",
    items: [
      {
        title: "Airport ↔ Port Hercule",
        description:
          "Direct Nice ↔ Monaco link for owners and guests arriving at Port Hercule, with flight tracking.",
      },
      {
        title: "UHNW clientele",
        description:
          "Owners, brokers and family offices: absolute discretion, a uniformed chauffeur and complete confidentiality.",
      },
      {
        title: "Crew runs",
        description:
          "Return trips for captains, crews and suppliers between quays, hotels and providers in the Principality.",
      },
      {
        title: "Quay access despite the crowds",
        description:
          "Port Hercule is closed to the public during the show: drop-off as close as possible to the gangways and stands within access.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Monaco Yacht Show",
    services: [
      {
        title: "Nice airport transfer",
        text: "Nice ↔ Monaco link with flight tracking and coordination with the Monaco heliport available.",
      },
      {
        title: "Port Hercule access",
        text: "Drop-off and pick-up as close as possible to the gangways, stands and tenders within the show's access.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to chain yacht viewings, broker meetings and business lunches.",
      },
      {
        title: "Owner & crew runs",
        text: "Discreet trips for owners, guests, captains and crews throughout the show.",
      },
      {
        title: "Evenings & private dinners",
        text: "On-board receptions, prestige dinners and evenings in the Principality, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Yacht Show",
    items: [
      {
        name: "Port Hercule",
        description: "Superyacht exhibition quays, stands and gangways.",
      },
      {
        name: "Monte-Carlo & Casino",
        description: "Palaces, receptions and prestige dinners.",
      },
      {
        name: "Monaco heliport (Fontvieille)",
        description: "Fast links with Nice for owners and guests.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, arrivals concentrated in late September.",
      },
      {
        name: "La Condamine & Fontvieille",
        description: "Suppliers, hotels and crew logistics.",
      },
    ],
  },
  generic: {
    heading: "The Monaco Yacht Show from a chauffeur's seat",
    subheading:
      "In late September, Port Hercule becomes the world showcase of superyachting — an audience of owners, brokers and UHNW.",
    paragraphs: [
      "Every late September, the Monaco Yacht Show gathers the world's largest superyachts at Port Hercule, with their owners, brokers and family offices. The port closes to the public and gangway access is by invitation: a chauffeur who knows the Principality drops you as close as possible to the stands and quays within the show's access.",
      "Arrivals concentrate at Nice Côte d'Azur airport. Flight tracking, a discreet welcome and a direct link to Port Hercule, with heliport coordination via Fontvieille available for the tightest schedules.",
      "The show blends yacht viewings, broker meetings, on-board dinners and crew runs. Hourly hire keeps the same chauffeur for owners and captains alike, with the absolute discretion a UHNW clientele expects and late-night returns after the receptions.",
    ],
    bullets: [
      "Drop-off nearest the gangways",
      "Flight tracking in late September",
      "UHNW discretion",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Monaco Yacht Show",
    steps: [
      "Tell us your dates, your yacht or hotel and your meetings (stands, brokers, dinners).",
      "We set the airport ↔ Port Hercule links and drop-offs within the show's access.",
      "The chauffeur arrives in uniform, with flight tracking and heliport coordination if needed.",
      "Day and evening hire for viewings, meetings and receptions.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur during the Monaco Yacht Show?",
      a: "Book ahead: in late September the Principality is in high demand. Send your dates, your yacht or hotel and your meetings so we can secure a dedicated chauffeur, with the discretion a UHNW clientele expects.",
    },
    {
      q: "Can you drop off as close as possible to the Port Hercule quays?",
      a: "Yes, within the show's access. During the Yacht Show the port is closed to the public; the chauffeur drops you at the nearest authorised point to the gangways and stands, then collects you within the access windows.",
    },
    {
      q: "Do you handle the Nice airport to Port Hercule link?",
      a: "Yes, with flight tracking and a discreet welcome. We run the direct Nice ↔ Monaco link and can coordinate with the Monaco heliport for owner and guest arrivals on tight schedules.",
    },
    {
      q: "Do you also cover crew and supplier trips?",
      a: "Yes. Beyond owners and guests, we organise captain, crew and supplier runs between quays, hotels and providers in the Principality throughout the show.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a Monaco ⇄ Nice Airport transfer",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Book a Monaco ⇄ Saint-Tropez transfer",
      href: "/en/chauffeur/transfers/monaco-saint-tropez",
    },
    {
      label: "Check availability",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Monaco Yacht Show",
    text: "Airport ↔ Port Hercule, crew runs and discreet service: book a dedicated chauffeur for the show.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title:
      "Monaco Yacht Show Chauffeur — UHNW car service Port Hercule | KS Rent Services",
    description:
      "Private chauffeur during the Monaco Yacht Show: airport ↔ Port Hercule transfers, owner and crew runs, heliport coordination and discreet UHNW service.",
    canonical: "/en/chauffeur/monaco-yacht-show",
  },
};

export default monacoYachtShow;
